import { Typography } from 'antd'
import React from 'react'
import moment from 'moment'

/**
 * A patient's age as a print shows it, in the print's language: years, or months under a year. It is their age on the
 * printed document's date (`at`), so a reprint says what the original did; today when it has none.
 */
export const getPrintAge = (birthDate, t, lng, at) => {
  if (!birthDate || !moment(birthDate).isValid()) return ''
  const on = at && moment(at).isValid() ? moment(at) : moment()
  const years = on.diff(moment(birthDate), 'years')
  if (years >= 1) return t('values.years', { count: years, lng })
  return t('values.months', { count: Math.max(on.diff(moment(birthDate), 'months'), 0), lng })
}

export const getLabelSpacing = title => (
  <>
    {title}
    <Typography.Text type="secondary">
      &nbsp;<sup>(mm)</sup>
    </Typography.Text>
  </>
)

export const fakePrescriptionData = {
  medications: [
    {
      id: 1,
      method: 'PO (By Mouth)',
      name: 'Ampicillin',
      strength: '250mg',
      type: 'Capsule',
      duration: 2,
      dosage: 2,
      details: 'After Food'
    },
    {
      id: 2,
      method: 'PO (By Mouth)',
      name: 'Amoxil',
      strength: '500mg',
      type: 'Capsule',
      duration: 4,
      dosage: 3,
      details: '1 Morning, 1 Night'
    },
    {
      id: 3,
      method: 'TP (Topical)',
      name: 'Alcagent',
      strength: '100ml m.wash',
      type: 'Mouth Wash',
      duration: 7,
      dosage: 1,
      details: 'Before Food'
    },
    {
      id: 4,
      method: 'TP (Topical)',
      name: 'Ab one',
      strength: '120ml m.wash',
      type: 'Mouth Wash',
      duration: 3,
      dosage: 2
    },
    {
      id: 5,
      method: 'PO (By Mouth)',
      name: 'Abimol extra',
      strength: '500mg',
      type: 'Tablet',
      duration: 5,
      dosage: 1
    }
  ],
  details: 'Extra details can be found here',
  doctor: { name: 'Beatrice R. Meador' },
  patient: {
    firstName: 'Louis',
    lastName: 'Hoover',
    birthDate: moment().subtract(21, 'y'),
    referenceId: 46,
    nationalId: 8829475012
  }
}

export const fakeInvoiceData = {
  details: 'Extra details can be found here',
  invoice: {
    diagnosticFee: 130,
    dfTax: 19.5,
    discount: 130,
    discountPercent: 11.5,
    insurance: 0,
    insuranceCompany: null,
    insurancePercent: 20,
    operations: [
      {
        amount: 1,
        price: 600,
        code: 'g3',
        id: 1,
        name: 'Bone grafting',
        procedure: { code: 'g3', id: 1, name: 'Bone grafting' },
        status: 'PLANNED',
        discount: 100,
        tax: 75,
        tooth: [28]
      },
      {
        amount: 1,
        price: 150,
        code: 'i3',
        id: 2,
        name: 'Permanent tooth extraction',
        procedure: { code: 'i3', id: 2, name: 'Permanent tooth extraction' },
        status: 'PLANNED',
        discount: 30,
        tax: 18,
        tooth: [15]
      },
      {
        amount: 1,
        price: 250,
        code: 'h4',
        id: 3,
        name: 'Invisalign ClinCheck',
        procedure: { code: 'h4', id: 3, name: 'Invisalign ClinCheck' },
        status: 'PLANNED',
        tax: 37.5,
        tooth: [2]
      }
    ],
    paid: 1150,
    paidInFull: true,
    payments: [
      { id: 1, amount: 500, type: 'CASH', refunded: false, createdAt: moment().subtract(1, 'M') },
      { id: 2, amount: 650, type: 'CARD', refunded: false, createdAt: moment() }
    ],
    pendingPayment: 0,
    pendingPaymentPercent: 0,
    subtotal: 1130,
    tax: 150,
    taxPercent: 15,
    total: 1150,
    referenceId: 25,
    patient: {
      firstName: 'Louis',
      lastName: 'Hoover',
      birthDate: moment().subtract(21, 'y'),
      referenceId: 46,
      nationalId: 1412414214,
      gender: 'male',
      phoneNumber: '+966 534342231',
      country: 'SA',
      nationality: 'SA',
      state: 'Riyadh',
      area: 'Al Raed',
      insuranceNumber: 1245324
    },
    doctor: { name: 'Ahmed Hassan', medicalNumber: '53274327932' },
    branch: {
      phones: ['+966 53434229', '+966 53434221'],
      address: 'Riyadh - Al Raed - Prince Turki Bin Abdulaziz Al Awal Rd - 12354'
    }
  },
  doctor: { name: 'Beatrice R. Meador' },
  patient: {
    firstName: 'Louis',
    lastName: 'Hoover',
    birthDate: moment().subtract(21, 'y'),
    referenceId: 46,
    nationalId: 8829475012
  }
}

export const fakeExpensesData = {
  expense: {
    name: 'Expense Name',
    amount: 100,
    date: moment(),
    type: 'MONTHLY_EXPENSES',
    other: 'Other',
    details: 'Extra details can be found here',
    createdBy: { name: 'Beatrice R. Meador' },
    createdAt: moment(),
    user: { name: 'Jane Doe' },
    branch: { name: 'Branch Name' }
  }
}

export const fakeLabOrderData = {
  labOrder: {
    name: 'Endo Crown',
    amount: 100,
    due: moment(),
    paid: false,
    received: false,
    tooth: [1, 2],
    child: false,
    shade: 'MR4',
    price: 1200,
    items: 1,
    details: 'Extra details can be found here',
    user: { name: 'Beatrice R. Meador' },
    lab: { name: 'Bright Lab' },
    branch: { name: 'Branch Name', address: 'New Cairo, Cairo Business Plaze', phones: ['+20_123123123'] },
    patient: { firstName: 'Martin', lastName: 'Hoover', referenceId: 123, nationalId: 8829475012 }
  }
}

export const fakeFreeFormData = {
  doctor: { name: 'Beatrice R. Meador' },
  patient: {
    firstName: 'Louis',
    lastName: 'Hoover',
    birthDate: moment().subtract(21, 'y'),
    referenceId: 124,
    nationalId: 8829475012
  },
  content:
    '<h2>Consent Title</h2><ul><li>First </li><li>Second</li><li>Third</li></ul><p><br></p><h3>Consent Content</h3><ol><li>I&nbsp;<u>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</u>&nbsp;({{PATIENT_NAME}}) give permission for&nbsp;<strong>[{{PRACTICE_NAME}}]</strong>&nbsp;to give me medical treatment.</li><li>I allow&nbsp;<strong>[{{PRACTICE_NAME}}]</strong>&nbsp;to file for insurance benefits to pay for the care I receive.&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</li><li>I understand that:</li></ol><ul><li class="ql-indent-1"><strong>[practice name]</strong>&nbsp;will have to send my medical record information to my insurance company.</li><li class="ql-indent-1">I must pay my share of the costs.</li><li class="ql-indent-1">I must pay for the cost of these services if my insurance does not pay or I do not have insurance</li><li class="ql-indent-1">I have the right to refuse any procedure or treatment.</li><li class="ql-indent-1">I have the right to discuss all medical treatments with my clinician.</li></ul><p><br></p><h3>Consent Form</h3><p><br></p><ol><li>I hereby authorize Health Care Services to use the telehealth practice platform for telecommunication for evaluating, testing and diagnosing my medical condition.</li><li>I understand that technical difficulties may occur before or during the telehealth sessions and my appointment cannot be started or ended as intended.</li><li>I accept that the professionals can contact interactive sessions with video call; however, I am informed that the sessions can be conducted via regular voice communication if the technical requirements such as internet speed cannot be met.</li></ol><p><br></p><p><span style="color: rgb(37, 45, 91);">A consent form is a signed document that outlines the informed consent of an individual for a medical study, clinical trial, or activity. Whether you’re looking for a way to gather model releases, activity waivers, parental consent, or medical consent forms, you can start by selecting one of our 400+ Consent Form Templates. Once you’ve chosen a consent form and customized its terms and conditions, all that’s left to do is embed the form on your website, send the link via email, or let participants fill out the form in person on your tablet or computer — you’ll then be able to securely collect consent forms online! You can even link your custom form to one of our temop to automatically turn form submissions into professional PDF documents.</span></p>'
}

export const pageSizes = [
  { value: 'A3', label: 'A3 (297 x 420mm)', width: 297, height: 420 },
  { value: 'A4', label: 'A4 (210 x 297mm)', width: 210, height: 297 },
  { value: 'A5', label: 'A5 (148 x 210mm)', width: 148, height: 210 },
  { value: 'A6', label: 'A6 (105 x 148mm)', width: 105, height: 148 },
  { value: 'B3', label: 'B3 (353 x 500mm)', width: 353, height: 500 },
  { value: 'B4', label: 'B4 (250 x 353mm)', width: 250, height: 353 },
  { value: 'B5', label: 'B5 (176 x 250mm)', width: 176, height: 250 },
  { value: 'B6', label: 'B6 (125 x 176mm)', width: 125, height: 176 },
  { value: 'C3', label: 'C3 (324 x 458mm)', width: 324, height: 458 },
  { value: 'C4', label: 'C4 (229 x 324mm)', width: 229, height: 324 },
  { value: 'C5', label: 'C5 (162 x 229mm)', width: 162, height: 229 },
  { value: 'C6', label: 'C6 (114 x 162mm)', width: 114, height: 162 },
  { value: 'C7', label: 'C7 (114 x 20mm)', width: 114, height: 20 },
  { value: 'HEAT_PAPER', label: 'HEAT PAPER (79 x 148mm)', width: 79, height: 148 },
  { value: 'LETTER', label: 'Letter (216 x 280mm)', width: 216, height: 280 },
  { value: 'LEGAL', label: 'Legal (216 x 356mm)', width: 216, height: 356 },
  { value: 'LEDGER', label: 'Ledger (279 x 431mm)', width: 279.4, height: 431.8 }
]

export const textPlacements = ['center', 'right', 'left']

export const layoutTypes = ['PORTRAIT', 'LANDSCAPE']

export const dateFormats = [
  { value: 'DD MMM YYYY', label: '14 Sep 2021' },
  { value: 'DD / MMM / YYYY', label: '14 / Sep / 2021' },
  { value: 'DD - MMM - YYYY', label: '14 - Sep - 2021' },
  { value: 'DD MMMM YYYY', label: '14 September 2021' },
  { value: 'DD / MMMM / YYYY', label: '14 / September / 2021' },
  { value: 'DD - MMMM - YYYY', label: '14 - September - 2021' },
  { value: 'DD MMM', label: '14 Sep' },
  { value: 'DD / MMM', label: '14 / Sep' },
  { value: 'DD - MMM', label: '14 - Sep' },
  { value: 'DD MM YYYY', label: '14 09 2021' },
  { value: 'DD / MM / YYYY', label: '14 / 09 / 2021' },
  { value: 'DD - MM - YYYY', label: '14 - 09 - 2021' },
  { value: 'DD MM', label: '14 09' },
  { value: 'DD / MM', label: '14 / 09' },
  { value: 'DD - MM', label: '14 - 09' },
  { value: 'DD MMM YYYY h:mm A', label: '14 Sep 2021 12:30 AM' },
  { value: 'DD / MMM / YYYY h:mm A', label: '14 / Sep / 2021 12:30 AM' },
  { value: 'DD - MMM - YYYY h:mm A', label: '14 - Sep - 2021 12:30 AM' },
  { value: 'DD MMMM YYYY h:mm A', label: '14 September 2021 12:30 AM' },
  { value: 'DD / MMMM / YYYY h:mm A', label: '14 / September / 2021 12:30 AM' },
  { value: 'DD - MMMM - YYYY h:mm A', label: '14 - September - 2021 12:30 AM' },
  { value: 'DD MMM h:mm A', label: '14 Sep 12:30 AM' },
  { value: 'DD / MMM h:mm A', label: '14 / Sep 12:30 AM' },
  { value: 'DD - MMM h:mm A', label: '14 - Sep 12:30 AM' },
  { value: 'DD MM YYYY h:mm A', label: '14 09 2021 12:30 AM' },
  { value: 'DD / MM / YYYY h:mm A', label: '14 / 09 / 2021 12:30 AM' },
  { value: 'DD - MM - YYYY h:mm A', label: '14 - 09 - 2021 12:30 AM' },
  { value: 'DD MM h:mm A', label: '14 09 12:30 AM' },
  { value: 'DD / MM h:mm A', label: '14 / 09 12:30 AM' },
  { value: 'DD - MM h:mm A', label: '14 - 09 12:30 AM' },
  { value: 'h:mm A', label: '12:30 PM' },
  { value: 'h:mm:ss A', label: '12:30:18 PM' },
  { value: 'MM/DD/YYYY', label: '08/14/2021' },
  { value: 'MMMM D, YYYY', label: 'September 14, 2021' },
  { value: 'MMMM D, YYYY h:mm A', label: 'September 14, 2021 12:30 PM' },
  { value: 'dddd, MMMM D, YYYY h:mm A', label: 'Thursday, September 14, 2021 12:30 PM' },
  { value: 'M/D/YYYY', label: '8/14/2021' },
  { value: 'MMM D, YYYY', label: 'Sep 14, 2021' },
  { value: 'MMM D, YYYY h:mm A', label: 'Sep 14, 2021 12:30 PM' },
  { value: 'ddd, MMM D, YYYY h:mm A', label: 'Thu, Sep 14, 2021 12:30 PM' }
]

export const timeFormats = [
  { value: 'DD MMM YYYY', label: '14 Sep 2021' },
  { value: 'DD/MMM/YYYY', label: '14 / Sep / 2021' },
  { value: 'DD-MMM-YYYY', label: '14 - Sep - 2021' },
  { value: 'DD/MM/YYYY', label: '14 / 09 / 2021' },
  { value: 'DD-MM-YYYY', label: '14 - 09 - 2021' },
  { value: 'MMM DD YYYY', label: 'Sep 14 2021' },
  { value: 'MMM/DD/YYYY', label: 'Sep / 14 / 2021' },
  { value: 'MMM-DD-YYYY', label: 'Sep - 14 - 2021' },
  { value: 'MM/DD/YYYY', label: '09 / 14 / 2021' },
  { value: 'MM-DD-YYYY', label: '09 - 14 - 2021' }
]

const defaultBranchFormData = {
  layout: 'PORTRAIT',
  bordered: true,
  showOperations: true,
  showPayments: false,
  hideInvoiceID: false,
  hidePatientID: false,
  dosage: true,
  duration: true,
  method: true,
  marginTop: 2,
  marginBottom: 2,
  marginRight: 2,
  marginLeft: 2,
  fontSize: 14,
  title: 'Title',
  subtitle: 'Subtitle',
  preFooter: 'Pre Header',
  pageSize: 'A4',
  showLogo: false,
  showQr: false,
  showHeader: false,
  showFooter: false,
  showName: true,
  showNameLabel: true,
  nameOnOwnLine: false,
  nameMarginTop: 0,
  nameMarginRight: 0,
  nameMarginLeft: 0,
  namePlacement: 'left',
  showAge: true,
  showAgeLabel: true,
  ageOnOwnLine: false,
  ageMarginTop: 0,
  ageMarginRight: 0,
  ageMarginLeft: 0,
  agePlacement: 'left',
  showDoctor: true,
  showDoctorLabel: true,
  patientSignature: true,
  showPatientName: true,
  showPatientDate: true,
  showPatientBorder: true,
  showDoctorBorder: true,
  doctorOnOwnLine: false,
  doctorMarginTop: 0,
  doctorMarginRight: 0,
  doctorMarginLeft: 0,
  doctorPlacement: 'left',
  showDate: true,
  showDateLabel: true,
  dateOnOwnLine: false,
  dateMarginTop: 0,
  dateMarginRight: 0,
  dateMarginLeft: 0,
  datePlacement: 'left',
  dateFormat: 'DD MMM YYYY',
  hasFooter: false,
  footer: 'Footer',
  showWatermark: false,
  useBlack: false,
  showWatermarkLogo: false,
  watermarkLogo: undefined,
  watermarkContent: 'Dentolize',
  watermarkColor: { r: 0, g: 0, b: 0, a: 0.15 },
  watermarkFontSize: 16,
  watermarkWidth: 120,
  watermarkHeight: 64,
  watermarkZIndex: 11,
  watermarkRotate: -22,
  watermarkGap: [100, 100],
  watermarkOffset: undefined
}

export const defaultJsonForm = JSON.stringify(defaultBranchFormData)
export const appointmentJsonForm = JSON.stringify({
  ...defaultBranchFormData,
  pageSize: 'HEAT_PAPER',
  showDoctor: false,
  showPatient: false,
  hidePatientID: true,
  showLogo: true,
  showQr: false,
  showPatientName: false,
  showDoctorName: false,
  showPatientDate: false,
  showDoctorDate: false,
  patientSignature: false,
  showHeader: false,
  showDate: false,
  showName: false,
  showAge: false,
  logoSize: 100
})

export const fontFamilies = [
  'Arial',
  'Arial Black',
  'Bahnschrift',
  'Calibri',
  'Cambria',
  'Cambria Math',
  'Candara',
  'Comic Sans MS',
  'Consolas',
  'Constantia',
  'Corbel',
  'Courier New',
  'Ebrima',
  'Franklin Gothic Medium',
  'Gabriola',
  'Gadugi',
  'Georgia',
  'HoloLens MDL2 Assets',
  'Impact',
  'Ink Free',
  'Javanese Text',
  'Leelawadee UI',
  'Lucida Console',
  'Lucida Sans Unicode',
  'Malgun Gothic',
  'Marlett',
  'Microsoft Himalaya',
  'Microsoft JhengHei',
  'Microsoft New Tai Lue',
  'Microsoft PhagsPa',
  'Microsoft Sans Serif',
  'Microsoft Tai Le',
  'Microsoft YaHei',
  'Microsoft Yi Baiti',
  'MingLiU-ExtB',
  'Mongolian Baiti',
  'MS Gothic',
  'MV Boli',
  'Myanmar Text',
  'Nirmala UI',
  'Palatino Linotype',
  'Segoe MDL2 Assets',
  'Segoe Print',
  'Segoe Script',
  'Segoe UI',
  'Segoe UI Historic',
  'Segoe UI Emoji',
  'Segoe UI Symbol',
  'SimSun',
  'Sitka',
  'Sylfaen',
  'Symbol',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
  'Webdings',
  'Wingdings',
  'Yu Gothic',
  'American Typewriter',
  'Andale Mono',
  'Arial Narrow',
  'Arial Rounded MT Bold',
  'Arial Unicode MS',
  'Avenir',
  'Avenir Next',
  'Avenir Next Condensed',
  'Baskerville',
  'Big Caslon',
  'Bodoni 72',
  'Bodoni 72 Oldstyle',
  'Bodoni 72 Smallcaps',
  'Bradley Hand',
  'Brush Script MT',
  'Chalkboard',
  'Chalkboard SE',
  'Chalkduster',
  'Charter',
  'Cochin',
  'Copperplate',
  'Courier',
  'Didot',
  'DIN Alternate',
  'DIN Condensed',
  'Futura',
  'Geneva',
  'Gill Sans',
  'Helvetica',
  'Helvetica Neue',
  'Herculanum',
  'Hoefler Text',
  'Lucida Grande',
  'Luminari',
  'Marker Felt',
  'Menlo',
  'Monaco',
  'Noteworthy',
  'Optima',
  'Palatino',
  'Papyrus',
  'Phosphate',
  'Rockwell',
  'Savoye LET',
  'SignPainter',
  'Skia',
  'Snell Roundhand',
  'Times',
  'Trattatello',
  'Zapfino'
]

export const selectorDateFormat = user =>
  user?.company?.extraSettings?.dateFormat
    ? [user.company.extraSettings.dateFormat.replace('DD', 'D'), user.company.extraSettings.dateFormat]
    : ['D/M/YYYY', 'DD/MM/YYYY']
